import { Fragment, VFC } from 'react';
import { Form, Space, UploadFile } from 'antd';
import { FormItem } from 'components/Form/FormItem';
import { Select } from 'components/Select/Select';
import { isUnit, Unit, UNIT_TRANSLATED } from '@enmon/commons';
import { ImageUpload } from 'components/ImageUpload/ImageUpload';
import { FormattedMessage, useIntl } from 'react-intl';
import { InputNumber } from 'components/Input/InputNumber';
import { RegisterState } from '../useStore';
import { RegisterStates as messages } from '../messages';

interface RegisterStatesProps {
  registerStates: RegisterState[];
  fieldName: string;
  selectedUnits?: (Unit | null)[];
  onPhotoSelect: (fieldName: number, fileList: UploadFile[]) => void;
}

export const RegisterStates: VFC<RegisterStatesProps> = ({
  fieldName,
  registerStates,
  selectedUnits,
  onPhotoSelect,
}) => {
  const intl = useIntl();
  const getAddonAfter = (index: number, field: number) => {
    const unit = selectedUnits?.[index];

    return isUnit(unit) ? (
      UNIT_TRANSLATED[unit]
    ) : (
      <FormItem noStyle name={[field, 'unit' satisfies keyof RegisterState]}>
        <Select
          options={[Unit.kWh, Unit.MWh, Unit.m3, Unit.GJ, Unit.MJ].map(unitOption => ({
            value: unitOption,
            label: UNIT_TRANSLATED[unitOption],
          }))}
          style={{ minWidth: 100 }}
        />
      </FormItem>
    );
  };

  return (
    <Form.List initialValue={registerStates} name={fieldName}>
      {fields =>
        fields.map((field, index) => (
          <Fragment key={field.key}>
            <Space>
              <FormattedMessage {...messages.register} values={{ register: index + 1 }} />
              <strong>
                {registerStates[field.name].name ?? '-'} ({registerStates[field.name].fullObis ?? '-'})
              </strong>
            </Space>
            <FormItem
              required
              label={<FormattedMessage {...messages.registerStatus} />}
              name={[field.name, 'counter' satisfies keyof RegisterState]}
              rules={[{ required: true, message: intl.formatMessage(messages.registerRequired) }]}
            >
              <InputNumber addonAfter={getAddonAfter(index, field.name)} className="w-100" placeholder="473.8" />
            </FormItem>
            <FormItem
              required
              label={<FormattedMessage {...messages.registerPhotos} />}
              name={[field.name, 'photos' satisfies keyof RegisterState]}
              rules={[
                {
                  required: true,
                  message: intl.formatMessage(messages.registerPhotosRequired),
                },
              ]}
            >
              <ImageUpload
                multiple
                fileList={registerStates?.[field.name]?.photos ?? []}
                name="photos"
                onImageUpload={fileList => onPhotoSelect(field.name, fileList)}
              />
            </FormItem>
          </Fragment>
        ))
      }
    </Form.List>
  );
};
